/**
 * Record despatched, for made lines (V1 completion E1): our own production sent part or all of a made line to its
 * customer address. A made line is despatched on its own, so each ticked line is its own despatch (its own
 * `despatch_ref`), sharing the date, carrier and tracking reference of the one run to the address.
 *
 * The lines, what is still to go and the URLs are the order view's `record_made_despatched`. The dialog makes one
 * reference when it opens and derives each line's from it, so a retry (after a refusal part-way, or a lost answer)
 * records each despatch once: orders replays an identical despatch and refuses a reference reused for another part.
 * `onRecord` posts one despatch and resolves to the refusal to show, or null once recorded.
 */
import { useId, useRef, useState } from "react";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import type {
  DeliveryGroupView,
  RecordMadeDespatched,
  RecordMadeLineDespatchedRequest,
} from "@shared-core/data/order-to-invoice-types";
import {
  CARRIERS,
  type Carrier,
} from "@shared-core/data/supplier-portal-adapter";
import { carrierLabel } from "@shared-core/domain/supplier-portal-wording";
import {
  londonTodayIso,
  type OrderStepFailure,
} from "@shared-core/domain/order-to-invoice-presentation";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Field, FailureBanner } from "./OrderDialogs";
import {
  consignmentDescription,
  consignmentQuantityProblem,
} from "./RecordDespatchedTogetherDialog";

const count = (n: number) => n.toLocaleString("en-GB");

interface Pick {
  isTicked: boolean;
  quantity: string;
}

export function RecordMadeDespatchedDialog({
  group,
  lines,
  busy,
  onClose,
  onRecord,
}: {
  /** The delivery group it was opened from: where the made lines are going. */
  group: DeliveryGroupView;
  lines: RecordMadeDespatched[];
  busy: boolean;
  onClose: () => void;
  onRecord: (
    action: RecordMadeDespatched,
    input: RecordMadeLineDespatchedRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const today = londonTodayIso();
  // One key per delivery line, made when first sent and kept for a retry of the run.
  const requestIds = useRef<Record<string, string>>({});
  const [on, setOn] = useState(today);
  const [carrier, setCarrier] = useState<Carrier | "">("");
  const [carrierName, setCarrierName] = useState("");
  const [tracking, setTracking] = useState("");
  const [picks, setPicks] = useState<Record<string, Pick>>(() =>
    Object.fromEntries(
      lines.map((l) => [
        l.delivery_line_id,
        { isTicked: true, quantity: String(l.outstanding_quantity) },
      ]),
    ),
  );
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);

  const ticked = lines.filter((l) => picks[l.delivery_line_id]?.isTicked);
  const quantityProblems = Object.fromEntries(
    ticked.map((l) => [
      l.delivery_line_id,
      consignmentQuantityProblem(
        picks[l.delivery_line_id]!.quantity,
        l.outstanding_quantity,
      ),
    ]),
  );
  const nothingTicked =
    ticked.length === 0 ? "Tick at least one line to record a despatch." : null;
  const dateProblem = !on
    ? "Enter a date."
    : on > today
      ? "The date can't be in the future."
      : undefined;
  const hasProblem =
    !!nothingTicked ||
    !!dateProblem ||
    Object.values(quantityProblems).some(Boolean) ||
    (carrier === "other" && carrierName.trim() === "");

  const setPick = (key: string, change: Partial<Pick>) =>
    setPicks((current) => ({
      ...current,
      [key]: { ...current[key]!, ...change },
    }));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Record despatched</DialogTitle>
          <DialogDescription>
            {consignmentDescription(group)} Made in-house: each line is recorded
            as its own despatch, and billing starts from the first.
          </DialogDescription>
        </DialogHeader>
        {/* noValidate: each check says why beside its field, where the browser's `max` would only block. */}
        <form
          noValidate
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setFailure(null);
            setFailedKey(null);
            if (hasProblem) return;
            for (const action of ticked) {
              const refused = await onRecord(action, {
                despatched_on: on,
                request_id: (requestIds.current[action.delivery_line_id] ??=
                  newUuid()),
                delivery_line_id: action.delivery_line_id,
                quantity: Number(picks[action.delivery_line_id]!.quantity),
                carrier: carrier || null,
                carrier_name: carrier === "other" ? carrierName.trim() : null,
                tracking_number:
                  carrier && carrier !== "own_van"
                    ? tracking.trim() || null
                    : null,
              });
              if (refused) {
                setFailure(refused);
                setFailedKey(action.delivery_line_id);
                return;
              }
            }
            onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <div className="grid gap-3 sm:grid-cols-3">
            <Field
              id={`${id}-on`}
              label="Despatched on"
              error={dateProblem ?? failure?.fieldErrors.despatched_on}
            >
              {(aria) => (
                <Input
                  {...aria}
                  type="date"
                  max={today}
                  value={on}
                  onChange={(e) => setOn(e.target.value)}
                />
              )}
            </Field>
            <Field
              id={`${id}-carrier`}
              label="Carrier"
              error={failure?.fieldErrors.carrier}
            >
              {(aria) => (
                <Select
                  value={carrier}
                  onValueChange={(value) => setCarrier(value as Carrier)}
                >
                  <SelectTrigger {...aria} className="w-full">
                    <SelectValue placeholder="Choose a carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    {CARRIERS.map((c) => (
                      <SelectItem key={c} value={c}>
                        {carrierLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field
              id={`${id}-tracking`}
              label="Tracking reference"
              error={failure?.fieldErrors.tracking_number}
            >
              {(aria) => (
                <Input
                  {...aria}
                  value={tracking}
                  maxLength={64}
                  autoComplete="off"
                  disabled={!carrier || carrier === "own_van"}
                  onChange={(e) => setTracking(e.target.value)}
                />
              )}
            </Field>
          </div>
          {carrier === "other" && (
            <Field
              id={`${id}-carrier-name`}
              label="Carrier's name"
              error={
                failure?.fieldErrors.carrier_name ??
                (carrierName.trim() === "" ? "Name the carrier." : undefined)
              }
            >
              {(aria) => (
                <Input
                  {...aria}
                  value={carrierName}
                  maxLength={100}
                  onChange={(e) => setCarrierName(e.target.value)}
                />
              )}
            </Field>
          )}
          <section className="space-y-2" aria-labelledby={`${id}-lines`}>
            <h3 id={`${id}-lines`} className="text-sm font-medium">
              Made lines
            </h3>
            <ul className="space-y-2">
              {lines.map((line) => {
                const key = line.delivery_line_id;
                const pick = picks[key]!;
                const label = `${line.line_no} ${line.title}`;
                const quantityError = pick.isTicked
                  ? (quantityProblems[key] ??
                    (failedKey === key
                      ? failure?.fieldErrors.quantity
                      : undefined))
                  : undefined;
                const inputId = `${id}-${key}-quantity`;
                return (
                  <li key={key} className="space-y-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                        <Checkbox
                          checked={pick.isTicked}
                          onCheckedChange={(c) =>
                            setPick(key, { isTicked: c === true })
                          }
                          aria-label={`Send ${label}`}
                        />
                        <span className="min-w-0 truncate">{label}</span>
                      </label>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {count(line.outstanding_quantity)} still to go
                      </span>
                      <Input
                        id={inputId}
                        inputMode="numeric"
                        className="h-8 w-24"
                        aria-label={`How many of ${label}`}
                        value={pick.quantity}
                        disabled={!pick.isTicked}
                        onChange={(e) =>
                          setPick(key, { quantity: e.target.value })
                        }
                        aria-invalid={quantityError ? true : undefined}
                        aria-describedby={
                          quantityError ? `${inputId}-error` : undefined
                        }
                      />
                    </div>
                    {quantityError && (
                      <p
                        id={`${inputId}-error`}
                        className="text-sm text-red-600"
                      >
                        {quantityError}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
          {nothingTicked && (
            <p role="alert" className="text-sm text-red-600">
              {nothingTicked}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || hasProblem}>
              Record despatched
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
